import { useT } from "@/i18n";
import { HiPause, HiVolumeUp } from "react-icons/hi";
import { AnswerLine, Hero, MissingMedia, OptionReview, ReadOnlyCard, Section } from "./ReadOnlyKit";
import type { ReadOnlyTemplateProps } from "./types";
import { useReviewAudio } from "./useReviewAudio";

const audioCircle = {
  display: "flex",
  width: 84,
  height: 84,
  borderRadius: 42,
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#033dfc",
  boxShadow: "0 8px 16px rgba(3, 61, 252, 0.25)",
  border: "none",
  cursor: "pointer",
} as const;

/**
 * A past phonics-encode play. The only template with no recording: the student picks or types a
 * spelling, so the review is the option list with their pick and the right answer marked.
 */
export function SoundToSpellingReadOnly({
  snapshot,
  response,
  score,
}: ReadOnlyTemplateProps<"soundToSpelling">) {
  const t = useT();
  const { audio, questionMode, correctAnswer, options } = snapshot.slideData;
  const { playingUrl, toggle } = useReviewAudio();

  const isPlaying = playingUrl === audio.src;
  const Glyph = isPlaying ? HiPause : HiVolumeUp;

  return (
    <ReadOnlyCard title={snapshot.name} score={score}>
      <Hero>
        {audio.src ? (
          <button
            type="button"
            onClick={() => toggle(audio.src ?? null)}
            aria-label={isPlaying ? t["readonly.stopTheSound"] : t["readonly.playTheSound"]}
            style={audioCircle}
          >
            <Glyph size={36} color="#ffffff" />
          </button>
        ) : (
          <MissingMedia label={t["readonly.theSound"]} />
        )}
      </Hero>

      {questionMode === "multiple_choice" && (options.length > 0 || correctAnswer) ? (
        <Section label={t["readonly.options"]}>
          <OptionReview
            options={options}
            correctAnswer={correctAnswer}
            studentAnswer={response.answer}
          />
        </Section>
      ) : (
        <Section label={t["readonly.whatYouTyped"]}>
          <AnswerLine
            studentAnswer={response.answer}
            correctAnswer={correctAnswer}
            isCorrect={response.isCorrect}
          />
        </Section>
      )}
    </ReadOnlyCard>
  );
}
